import { ChatModal } from "@/components/Chat";
import { FollowerDialog } from "@/components/FollowerModal";
import GuideTooltip from "@/components/GuideTooltip";
import HBIcon from "@/components/HBIcon";
import HBMessage from "@/components/HBMessage";
import Image from "@/components/Image";
import { useLogin } from "@/components/LoginModal";
import { AdditionGoodDialog, BuyVipDialog } from "@/components/Vip";
import { getMuseName, isCertifyCompany } from "@/constants/areas";
import useAppStore from "@/context/store";
import displayNum from "@/utils/displayNum";
import imageUrl, { gdOssUrl } from "@/utils/imageUrl";
import isEditingElement from "@/utils/isEditingElement";
import useOuterClick from "@/utils/useOuterClick";

import {
  useHover,
  useKeyPress,
  useLocalStorageState,
  useSessionStorageState,
} from "ahooks";
import { Dropdown } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import Link from "next/link";
import { useRouter } from "next/router";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Case, Switch } from "react-if";
import { VipCard } from "./card";
import styles from "./index.module.less";
import { useInitRedTipState, useRedTipState } from "./redTipState";

dayjs.extend(duration);

export default function Avatar() {
  const { user, config, unPayOrders } = useAppStore(
    ({ user, config, unPayOrders }) => ({
      user,
      config,
      unPayOrders,
    })
  );
  const { logout } = useLogin();
  const [chatVisible, setChatVisible] = useState(false);
  const avatarRef = useRef(null);
  const [dropRef, dropVisible, setDropVisible] = useOuterClick(false, {
    extraTargets: [avatarRef],
  });
  const timeOutRef = useRef();
  const router = useRouter();

  // const [tipVisible, setTipVisible] = useLocalStorage(
  //   (userId) => `_PINIT_FRONT_USER_GUIDE_SIGN_${userId}_1_`,
  //   {
  //     loadingValue: false,
  //     defaultValue: true,
  //     needUserId: true,
  //   }
  // );

  // 红点标识
  useInitRedTipState();
  const { couponTip, orderTip } = useRedTipState((store) => store);
  const avatarRedTip = useMemo(() => {
    return couponTip || orderTip;
  }, [couponTip, orderTip]);

  // 快捷键：消息弹窗
  useKeyPress(
    "m",
    (e) => {
      const editing = isEditingElement();
      if (editing || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) {
        return false;
      }

      if (chatVisible) {
        setChatVisible(false);
      } else {
        setChatVisible(true);
      }
    },
    { exactMatch: true }
  );

  const [hideOrderTooltip, setHideOrderTooltip] =
    useSessionStorageState("showOrderTooltip");

  // const isPro = user?.extra?.pro;

  const avatarImg = useMemo(() => {
    if (user?.avatar) {
      // 目前头像最小是 75宽度
      return imageUrl(user.avatar, { width: 75, rect: "sq" });
    } else {
      return gdOssUrl(
        "https://cdn.dancf.com/fe-assets/img/c62d185173c48b4c73f90ea6b7acee33.png"
      );
    }
  }, [user?.avatar]);

  const showDrop = () => {
    // setTipVisible(false);
    clearTimeout(timeOutRef.current);
    setDropVisible(true);
  };

  const hideDrop = () => {
    timeOutRef.current = setTimeout(() => setDropVisible(false), 200);
  };

  return (
    <>
      {/* <GuideTooltip
        visible={tipVisible}
        title={"查看「我的花瓣」"}
        content={"点击头像，可以进入「我的花瓣」管理采集及查看关注信息~"}
        placement={"bottomLeft"}
        width={"280px"}
        onClose={() => setTipVisible(false)}
      > */}
      <Dropdown
        className={styles.avatar}
        getPopupContainer={(triggerNode) => triggerNode}
        destroyPopupOnHide
        overlay={
          <AvatarDrop
            user={user}
            config={config}
            avatarImg={avatarImg}
            ref={dropRef}
            onHide={hideDrop}
            onShow={showDrop}
            unPayOrders={unPayOrders}
            logout={logout}
            openBuyVipDialog={() => BuyVipDialog.open()}
            openAddition={AdditionGoodDialog.open}
            openChatModal={() => {
              setChatVisible(true);
            }}
          />
        }
        visible={dropVisible}
        placement="bottomRight"
      >
        <GuideTooltip
          visible={
            !hideOrderTooltip &&
            unPayOrders.length > 0 &&
            router.asPath !== "/me/order"
          }
          overlayClassName={styles.orderTooltip}
          title={null}
          content={
            <Link href={"/me/order"} passHref>
              <a>
                你有一笔待支付订单， 立即处理 <HBIcon type="ic_chevron_right" />
              </a>
            </Link>
          }
          placement={"bottomLeft"}
          width={"278px"}
          hideAction
          onClose={() => setHideOrderTooltip(true)}
          getPopupContainer={() => avatarRef.current}
        >
          <div
            ref={avatarRef}
            onMouseEnter={showDrop}
            onMouseLeave={hideDrop}
            className="header-item"
            style={{
              padding: "4px 0 4px 4px",
            }}
          >
            <Link href={"/user/" + user?.urlname}>
              <a
                className={classNames(
                  { [styles.unread]: avatarRedTip },
                  styles.avatarWrap
                )}
              >
                <Image
                  src={avatarImg}
                  alt={user?.username}
                  width="32"
                  height="32"
                />
                {/* {!!isPro && (
                  <div className={styles.pro}>
                    <HBIcon
                      type="a-bianzu6"
                      style={{
                        backgroundImage:
                          "radial-gradient(#fff 40%, #ffffff00 20%)",
                      }}
                    />
                  </div>
                )} */}
              </a>
            </Link>

            <HBIcon type="ic_arrow_down" className={styles.downicon} />
          </div>
        </GuideTooltip>
      </Dropdown>
      {/* </GuideTooltip> */}
      <ChatModal visible={chatVisible} onCancel={() => setChatVisible(false)} />
    </>
  );
}

export const AvatarDrop = forwardRef(function RawAvatarDrop(
  { avatarImg, user, config, onHide, onShow, logout },
  ref
) {
  const router = useRouter();
  const isHovering = useHover(() =>
    document.querySelector(`.${styles.username}`)
  );
  //const unreadNum = useRedTipState((store) => store.dmUnread);
  const isPro = user?.extra?.pro;

  const [showCertifyTip, setShowCertifyTip] = useLocalStorageState(
    `certifyTip_user_${user?.user_id}`,
    {
      defaultValue: true,
    }
  );
  const [historyTip, setHistoryTip] = useLocalStorageState(
    `_PINIT_FRONT_USER_GUIDE_SIGN_HISTORY_TIP_`,
    {
      defaultValue: true,
    }
  );

  const OptionList = [
    {
      icon: "register",
      name: "花瓣认证",
      url: "/muse/register/",
      iconStyle: {
        fontSize: 19,
        marginTop: 2,
      },
      showRedTip: showCertifyTip,
      action: (evl) => {
        const showRedTip = evl.showRedTip;
        if (showRedTip) {
          setShowCertifyTip(false);
        }
        // todo 点击测试
        // else {
        //   evl.showRedTip = true;
        //   setShowCertifyTip(true);
        // }
        window.open(evl.url, "_blank");
      },
    },
    {
      icon: "ic_pro",
      name: "花瓣Pro",
      isBlank: true,
      url: isPro ? "https://pro.huaban.com/my/vip" : "https://pro.huaban.com/",
    },
    {
      icon: "ic_gzguanli",
      name: "管理关注",
      url: `/user/${user?.urlname}/following`,
    },
    {
      icon: "ic_settings",
      name: "个人设置",
      url: "/settings",
    },
    {
      icon: "recent_fill",
      name: "浏览记录",
      url: "/history",
      showRedTip: historyTip,
      action: (evl) => {
        const showRedTip = evl.showRedTip;
        if (showRedTip) {
          setHistoryTip(false);
        }
        router.push(evl.url);
      },
    },
    {
      icon: "ic_exit",
      name: "退出花瓣",
      action: () => {
        onHide();
        logout();
      },
    },
  ];

  useEffect(() => {
    if (showCertifyTip) {
      if (!user?.extra?.is_museuser) {
        setShowCertifyTip(true);
      }
    }
  }, [user, showCertifyTip, setShowCertifyTip]);

  const jobName = useMemo(() => {
    if (user?.extra?.is_museuser) {
      const name = isCertifyCompany(user?.museUser?.category[0])
        ? user?.museUser.name
        : getMuseName(user?.museUser?.category[0]);
      return name || "认证设计师";
    } else {
      return user?.profile?.job_name;
    }
  }, [user]);

  const onUserIdCopy = (id) => {
    navigator.clipboard.writeText(id);
    HBMessage.success("用户ID已复制");
  };

  const isOffical = useMemo(() => {
    return config?.officialUsers?.split(",").includes(user?.urlname);
  }, [config, user]);

  if (!user) {
    return null;
  }

  return (
    <div
      className={styles.avatarDrop}
      ref={ref}
      onMouseLeave={onHide}
      onMouseEnter={onShow}
      data-content-source="个人头像下拉框"
      data-dialog-title="个人头像下拉框"
      data-gd-expose="dialog_expose"
    >
      <div className={styles.user}>
        <Link href={`/user/${user.urlname}`} passHref>
          <a
            className={classNames(styles.userLink, {
              [styles.hover]: isHovering,
            })}
          >
            <Image src={avatarImg} alt={user.username} width="48" height="48" />
          </a>
        </Link>
        <div>
          <Link href={`/user/${user.urlname}`} passHref>
            <a className={styles.username}>
              <span className={styles.text}>{user.username}</span>
            </a>
          </Link>
          <Switch>
            <Case condition={isOffical}>
              <div className={styles.offical} title="花瓣官方"></div>
            </Case>
            <Case condition={user?.extra?.is_museuser}>
              <Link href={`/muse/register`} passHref>
                <a className={styles.designer}>
                  <HBIcon
                    className={classNames([
                      isCertifyCompany(user?.museUser?.category[0])
                        ? styles.company
                        : styles.personal,
                    ])}
                    type="register"
                    title={
                      user?.extra?.is_museuser
                        ? isCertifyCompany(user?.museUser?.category[0])
                          ? "认证企业"
                          : "认证设计师"
                        : "未认证"
                    }
                    style={
                      user?.extra?.is_museuser
                        ? isCertifyCompany(user?.museUser?.category[0])
                          ? { color: "#298DFF" }
                          : { color: "#ff5967" }
                        : {}
                    }
                  />
                </a>
              </Link>
            </Case>
          </Switch>
          <Link href={`/user/${user.urlname}`} passHref>
            <a className={styles.userMore}>
              <HBIcon type="ic_chevron_right" />
            </a>
          </Link>
          <span className={styles.job}>
            {user?.profile?.job_name ? (
              jobName
            ) : (
              <Link href={"/settings"} passHref>
                <a
                  className={styles.setJob}
                  data-dialog-action="点击-设置职业"
                  data-gd-click="dialog_action"
                >
                  职业未设置
                </a>
              </Link>
            )}
            <span
              className={styles.userId}
              onClick={() => onUserIdCopy(user.user_id)}
            >
              用户ID: {user.user_id} <HBIcon type="copy" />
            </span>
          </span>
        </div>
      </div>
      <ul className={styles.info}>
        <li data-dialog-action="点击-画板" data-gd-click="dialog_action">
          <Link href={`/user/${user.urlname}`} passHref>
            <a>
              <span>{displayNum(user.board_count)}</span>
              <span>画板</span>
            </a>
          </Link>
        </li>
        <li data-dialog-action="点击-采集" data-gd-click="dialog_action">
          <Link href={`/user/${user.urlname}/pins`} passHref>
            <a>
              <span>{displayNum(user.pin_count)}</span>
              <span>采集</span>
            </a>
          </Link>
        </li>

        <li data-dialog-action="点击-关注" data-gd-click="dialog_action">
          <Link href={`/user/${user.urlname}/following`} passHref>
            <a>
              <span>{displayNum(user.following_count)}</span>
              <span>关注</span>
            </a>
          </Link>
        </li>

        <li
          onClick={() => {
            FollowerDialog.open({ urlname: user.urlname });
          }}
          data-dialog-action="点击-粉丝"
          data-gd-click="dialog_action"
        >
          <a>
            <span>{displayNum(user.follower_count)}</span>
            <span>粉丝</span>
          </a>
        </li>
      </ul>
      <div className={styles.cardList}>
        <VipCard />
      </div>
      <div className={styles.operationList}>
        {OptionList.map((item) => {
          // if (item.type === "separate") {
          //   return <div key={index} className="separate"></div>;
          // }
          if (item.action) {
            return (
              <Link href={item.url || ""} key={item.name}>
                <a
                  className={styles.item}
                  onClick={(e) => {
                    e.preventDefault();
                    item.action(item);
                  }}
                  data-dialog-action={`点击-${item.name}`}
                  data-gd-click="dialog_action"
                  data-gd-expose="dialog_expose"
                  key={item.name}
                >
                  {item.icon && (
                    <HBIcon
                      className={classNames({
                        [styles.unread]: !!item.unreadNum,
                        [styles.redTip]: item.showRedTip,
                      })}
                      type={item.icon}
                      style={{
                        fontSize: item.iconSize || "20px",
                        color: item.iconColor || "#505153",
                        ...(item.iconStyle || {}),
                      }}
                    />
                  )}
                  <span>{item.name}</span>
                </a>
              </Link>
            );
          }

          // 其他默认item
          return (
            <Link href={item.url} key={item.name}>
              <a
                className={styles.item}
                target={item.isBlank ? "_blank" : "_self"}
                data-dialog-action={`点击-${item.name}`}
                data-gd-click="dialog_action"
              >
                {item.icon && (
                  <HBIcon
                    type={item.icon}
                    style={{
                      fontSize: item.iconSize || "20px",
                      color: item.iconColor || "#505153",
                      ...(item.iconStyle || {}),
                    }}
                    className={classNames({ [styles.redTip]: item.showRedTip })}
                  />
                )}
                <span>{item.name}</span>
              </a>
            </Link>
          );
        })}
      </div>
    </div>
  );
});
